import React from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { flatten, get, snakeCase } from 'lodash';

import { useStyles } from '~/assets/styles';
import Grid from '~/components/core/Atomic/Grid/Grid';
import InnerCard from '~/components/core/Cards/InnerCard';
import TextFieldFormik from '~/components/TextFieldFormik';

import cn from '../../../../../../Utils/cn';
import { getObjectPaths } from '../../../../../../Utils/index';
import TrashIcon from '../../../../../icons/TrashIcon';
import InlineIconButton from '../../../../../InlineIconButton';
import ReorderDragHandle from '../../components/ReorderDragHandle';
import copies from '../../copies.json';
import { isNewRow } from '../../newRowIdentity';

const ID_PREFIX = 'configuration.categories';

const CategoryCard = ({ index, arrayHelpers, dragHandleProps }) => {
  const { initialValues, values, setFieldValue, setFieldTouched } = useFormikContext();
  const { classes } = useStyles();

  const field = get(values, `${ID_PREFIX}[${index}]`, {});
  // "Saved" must be decided by the category's own identity, not by its array position and not by the
  // key it is currently spelling. Rows are keyed by position, so after a DnD reorder
  // initialValues[index] belongs to a different category; a position check would treat a new
  // category moved onto a saved slot as saved, locking its Key and stopping the Display Name from
  // syncing it. The id alone is no better while a row is new, because the Display Name re-derives it
  // on every keystroke: the moment it spells a saved category's key the row would read as saved and
  // lock itself out for good. So a category added in this editing session is never saved, and one
  // that came from the server - which carries no marker - matches on the id it keeps.
  const initialCategories = get(initialValues, ID_PREFIX, []);
  const isSavedField = !isNewRow(field) && !!field.id && initialCategories.some((category) => category.id === field.id);
  const paths = getObjectPaths(values)?.filter((path) => path.split('.').includes('incident_sub_type_categories'));
  const isCategoryUsed = flatten(paths?.map((path) => get(values, path, [])))?.includes(field.id);
  const onDelete = () => arrayHelpers.remove(index);

  return (
    // In a DnD row the InnerCard's bottom margin is trapped inside the draggable flex item
    // and shows as a white strip under the card while dragging; the list gap handles row
    // spacing, so drop it when the row is draggable.
    <InnerCard className={dragHandleProps ? '!mb-0' : undefined}>
      <Grid container spacing={3}>
        <Grid item xs={5}>
          <TextFieldFormik
            id={`${ID_PREFIX}[${index}].desc`}
            label="Display Name"
            fullWidth
            // Sync the Key from the Display Name only while the user types (edit-driven), not via
            // a value-watching effect: rows are keyed by position, so a reorder shuffles data
            // between positions and a watcher would read that as an edit and rewrite the key.
            onChange={(e) => {
              const newDesc = e.target.value;
              setFieldValue(`${ID_PREFIX}[${index}].desc`, newDesc);
              if (!isSavedField) {
                setFieldValue(`${ID_PREFIX}[${index}].id`, snakeCase(newDesc));
                setFieldTouched(`${ID_PREFIX}[${index}].id`, false);
              }
            }}
          />
        </Grid>
        <Grid item xs={5}>
          <TextFieldFormik id={`${ID_PREFIX}[${index}].id`} label="Key" fullWidth disabled={isSavedField} />
        </Grid>
        <Grid item xs={2}>
          <div className="flex items-center justify-center gap-8 p-16">
            {dragHandleProps ? <ReorderDragHandle dragHandleProps={dragHandleProps} /> : null}
            <InlineIconButton
              icon={TrashIcon}
              tooltipTitle={isCategoryUsed ? copies.categories.category_in_use : copies.categories.remove}
              iconStyle={{ display: 'flex' }}
              className={cn(classes.textIcon, classes.hoverableNonFilledIcon)}
              onClick={onDelete}
              hideErrorGap
              disabled={isCategoryUsed}
            />
          </div>
        </Grid>
      </Grid>
    </InnerCard>
  );
};

CategoryCard.propTypes = {
  index: PropTypes.number.isRequired,
  arrayHelpers: PropTypes.object.isRequired,
  dragHandleProps: PropTypes.object,
};

export default CategoryCard;
